import React from 'react';
import {
  AlertCircle,
  AlertTriangle,
  BrainCircuit,
  CheckCircle2,
  Clock3,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';

const priorityStyles = {
  critical: { icon: ShieldAlert, border: 'border-red-300', badge: 'bg-red-700 text-white', iconColor: 'text-red-700' },
  high: { icon: AlertCircle, border: 'border-red-200', badge: 'bg-red-100 text-red-800', iconColor: 'text-red-600' },
  medium: { icon: AlertTriangle, border: 'border-amber-200', badge: 'bg-amber-100 text-amber-800', iconColor: 'text-amber-600' },
  low: { icon: CheckCircle2, border: 'border-sky-200', badge: 'bg-sky-100 text-sky-800', iconColor: 'text-sky-700' },
};

const RecommendationItem = ({ recommendation, index }) => {
  const style = priorityStyles[recommendation.priority] || priorityStyles.medium;
  const Icon = style.icon;
  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: Math.min(index * 0.05, 0.35) }}
      className={`border-l-4 bg-white p-5 ${style.border}`}
      data-testid={`ai-recommendation-${index}`}
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <Icon className={`mt-0.5 h-5 w-5 flex-none ${style.iconColor}`} />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold text-slate-950" data-testid={`ai-recommendation-${index}-title`}>
                {recommendation.title}
              </h3>
              <span className={`px-2 py-1 text-xs font-bold uppercase ${style.badge}`} data-testid={`ai-recommendation-${index}-priority`}>
                {recommendation.priority}
              </span>
            </div>
            <p className="mt-2 text-sm leading-6 text-slate-700" data-testid={`ai-recommendation-${index}-finding`}>
              {recommendation.finding}
            </p>
          </div>
        </div>
        <div className="flex flex-none items-center gap-2 text-xs text-slate-600" data-testid={`ai-recommendation-${index}-timeframe`}>
          <Clock3 className="h-4 w-4" />{recommendation.timeframe}
        </div>
      </div>

      <div className="mt-5 grid gap-5 border-t border-slate-200 pt-5 lg:grid-cols-2">
        <div>
          <p className="text-xs font-bold uppercase text-slate-500">Recommended actions</p>
          <ol className="mt-2 space-y-2 text-sm text-slate-700">
            {recommendation.action_items.map((action, actionIndex) => (
              <li key={action} className="flex gap-2" data-testid={`ai-recommendation-${index}-action-${actionIndex}`}>
                <span className="font-semibold text-slate-500">{actionIndex + 1}.</span><span>{action}</span>
              </li>
            ))}
          </ol>
        </div>
        <div>
          <p className="text-xs font-bold uppercase text-slate-500">Evidence to retain</p>
          <ul className="mt-2 space-y-2 text-sm text-slate-700">
            {recommendation.evidence_to_collect.map((evidence, evidenceIndex) => (
              <li key={evidence} className="flex gap-2" data-testid={`ai-recommendation-${index}-evidence-${evidenceIndex}`}>
                <span aria-hidden="true">•</span><span>{evidence}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-5 grid gap-3 bg-slate-50 p-4 text-xs text-slate-700 sm:grid-cols-3">
        <p data-testid={`ai-recommendation-${index}-controls`}><strong>Controls:</strong> {recommendation.control_ids.join(', ')}</p>
        <p data-testid={`ai-recommendation-${index}-owner`}><strong>Owner:</strong> {recommendation.owner}</p>
        <p data-testid={`ai-recommendation-${index}-category`}><strong>Category:</strong> {recommendation.category}</p>
        <p className="sm:col-span-3" data-testid={`ai-recommendation-${index}-benchmark`}><strong>Benchmark:</strong> {recommendation.benchmark_alignment}</p>
      </div>
    </motion.article>
  );
};

export const AIRecommendationsPanel = ({ loading, error, report, onRetry }) => (
  <section className="mb-8 border border-slate-300 bg-slate-50" data-testid="ai-recommendations-panel">
    <div className="flex flex-col justify-between gap-4 border-b border-slate-300 bg-slate-900 p-6 text-white sm:flex-row sm:items-start">
      <div className="flex items-start gap-3">
        <BrainCircuit className="mt-1 h-6 w-6 text-emerald-400" />
        <div>
          <h2 className="text-xl font-semibold" data-testid="ai-recommendations-title">AI-prioritized implementation plan</h2>
          <p className="mt-1 text-sm text-slate-300">Evidence-grounded ISO 27001:2022 recommendations for this Pro assessment.</p>
        </div>
      </div>
      {report && (
        <div className="text-xs text-slate-300 sm:text-right" data-testid="ai-recommendations-source">
          <p>Claude Sonnet 5.5</p>
          <p>{report.cache_status === 'cached' ? 'Retrieved from secure cache' : 'Generated for current answers'}</p>
        </div>
      )}
    </div>

    {loading && (
      <div className="p-8" data-testid="ai-recommendations-loading">
        <div className="flex items-center gap-3 text-slate-700"><RefreshCw className="h-5 w-5 animate-spin" />Analyzing all 93 controls and organization context…</div>
        <div className="mt-5 h-2 overflow-hidden bg-slate-200"><motion.div className="h-full w-1/3 bg-emerald-600" animate={{ x: ['-100%', '300%'] }} transition={{ duration: 1.5, repeat: Infinity }} /></div>
      </div>
    )}

    {!loading && error && (
      <div className="flex flex-col items-start gap-4 p-6 sm:flex-row sm:items-center sm:justify-between" data-testid="ai-recommendations-error">
        <div className="flex items-start gap-3"><AlertCircle className="mt-0.5 h-5 w-5 text-red-600" /><p className="text-sm text-slate-700">{error}</p></div>
        <Button variant="outline" onClick={onRetry} data-testid="ai-recommendations-retry-button"><RefreshCw className="mr-2 h-4 w-4" />Retry analysis</Button>
      </div>
    )}

    {!loading && report && (
      <div className="p-4 sm:p-6">
        <div className="mb-5 border border-slate-200 bg-white p-5" data-testid="ai-recommendations-executive-summary">
          <p className="text-xs font-bold uppercase text-slate-500">Executive summary</p>
          <p className="mt-2 text-sm leading-6 text-slate-700">{report.executive_summary}</p>
        </div>
        <div className="mb-5 flex flex-wrap gap-2" data-testid="ai-recommendations-priority-counts">
          {Object.entries(report.priority_counts).map(([priority, count]) => (
            <span key={priority} className={`px-3 py-1 text-xs font-semibold uppercase ${priorityStyles[priority]?.badge || 'bg-slate-200 text-slate-800'}`} data-testid={`ai-recommendations-${priority}-count`}>
              {priority}: {count}
            </span>
          ))}
        </div>
        <div className="space-y-4">
          {report.recommendations.map((recommendation, index) => <RecommendationItem key={`${recommendation.priority}-${recommendation.title}`} recommendation={recommendation} index={index} />)}
        </div>
        <p className="mt-6 border-t border-slate-200 pt-3 text-right text-[10px] leading-4 text-slate-400" data-testid="ai-recommendations-advisory">
          Recommendations are based on questionnaire responses and may differ from real-world conditions. Independent professional validation is advised.
        </p>
      </div>
    )}
  </section>
);